import { useEffect } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { Banner, Button } from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { getCurrentUser } from '@entities/user'
import { useResendVerificationEmailMutation, useVerifyEmailMutation } from '../api/auth.mutations'
import { getSearchToken, navigateTo } from './auth-routing'
import { actions, form, link } from './auth.css'
import { AuthLayout } from './AuthLayout'

type VerifyEmailPageProps = {
  token?: string
}

export function VerifyEmailPage({ token }: VerifyEmailPageProps) {
  const effectiveToken = token ?? getSearchToken()
  const currentToken = useSession((state) => state.token)
  const setUser = useSession((state) => state.setUser)
  const verifyMutation = useVerifyEmailMutation()
  const resendMutation = useResendVerificationEmailMutation()
  useEffect(() => {
    // Stop after an error too: the mutation object changes on every render, so without the
    // isError guard a failed verification retries (and toasts) in a loop.
    if (
      !effectiveToken ||
      verifyMutation.isPending ||
      verifyMutation.isSuccess ||
      verifyMutation.isError
    ) {
      return
    }

    verifyMutation.mutate(
      { token: effectiveToken },
      {
        onSuccess: () => {
          if (currentToken) {
            void getCurrentUser()
              .then(setUser)
              .catch(() => undefined)
          }
          toast.success('E-mail verificado.')
        },
        onError: (error) => {
          toast.error(
            getUserFacingError(error, {
              title: 'Não foi possível verificar o e-mail',
              fallback: 'Solicite um novo link e tente novamente.',
            }),
          )
        },
      },
    )
  }, [currentToken, effectiveToken, setUser, verifyMutation])

  function resendLink() {
    resendMutation.mutate(undefined, {
      onSuccess: () => toast.success('E-mail de confirmação reenviado.'),
      onError: (error) =>
        toast.error(
          getUserFacingError(error, {
            title: 'Não foi possível reenviar o e-mail',
            fallback: 'Tente novamente em alguns instantes.',
          }),
        ),
    })
  }

  return (
    <AuthLayout
      title="Verificação de e-mail"
      subtitle={
        verifyMutation.isError
          ? 'Confira o link recebido ou solicite um novo e-mail de verificação.'
          : 'Confirmamos o token para liberar sua conta com segurança.'
      }
    >
      <div className={form}>
        {!effectiveToken ? (
          <Banner
            tone="danger"
            title="Link inválido"
            action={
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={resendMutation.isPending}
                onClick={resendLink}
              >
                {resendMutation.isPending ? 'Enviando...' : 'Reenviar link'}
              </Button>
            }
          >
            O token de verificação não foi encontrado.
          </Banner>
        ) : null}
        {verifyMutation.isPending ? (
          <Banner tone="info" title="Verificando">
            Aguarde enquanto confirmamos seu e-mail.
          </Banner>
        ) : null}
        {verifyMutation.isSuccess ? (
          <Banner tone="success" title="E-mail verificado" icon={<CheckCircle2 size={18} />}>
            Você já pode continuar usando a VendePay.
          </Banner>
        ) : null}
        {verifyMutation.isError ? (
          <Banner
            tone="danger"
            title="Não foi possível verificar"
            action={
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={resendMutation.isPending}
                onClick={resendLink}
              >
                {resendMutation.isPending ? 'Enviando...' : 'Reenviar link'}
              </Button>
            }
          >
            Solicite um novo link de verificação.
          </Banner>
        ) : null}
        <div className={actions}>
          <Button
            type="button"
            fullWidth
            onClick={() => navigateTo(currentToken ? '/onboarding' : '/login')}
          >
            Continuar
          </Button>
          <a className={link} href="/login">
            Ir para login
          </a>
        </div>
      </div>
    </AuthLayout>
  )
}
